import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import {
  MessageSquare,
  Send,
  X,
  Search,
  User,
  Clock,
  Check,
  CheckCheck,
  AtSign,
  Paperclip,
  Mic,
  Phone,
  Video,
  MoreVertical,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { indexedDBService } from '../utils/offline/indexedDB';

interface Message {
  id: string;
  threadId: string;
  senderId: string;
  senderName: string;
  content: string;
  timestamp: number;
  read: boolean;
  mentions?: string[];
  attachments?: Array<{ id: string; name: string; type: string; url: string }>;
  replyTo?: string;
}

interface Thread {
  id: string;
  participants: Array<{ id: string; name: string; role: string; avatar?: string }>;
  lastMessage?: Message;
  unreadCount: number;
  title?: string;
  type: 'direct' | 'group' | 'well';
  wellId?: string;
}

interface MessagingCenterProps {
  currentUserId: string;
  currentUserName: string;
  onClose?: () => void;
}

export default function MessagingCenter({ currentUserId, currentUserName, onClose }: MessagingCenterProps) {
  const [threads, setThreads] = useState<Thread[]>([]);
  const [selectedThread, setSelectedThread] = useState<Thread | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadThreads();
  }, []);

  useEffect(() => {
    if (selectedThread) {
      loadMessages(selectedThread.id);
    }
  }, [selectedThread]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadThreads = async () => {
    // Mock threads - in production, load from IndexedDB
    const mockThreads: Thread[] = [
      {
        id: 'thread-1',
        participants: [
          { id: 'user-2', name: 'John Smith', role: 'Supervisor' },
          { id: currentUserId, name: currentUserName, role: 'Pumper' },
        ],
        lastMessage: {
          id: 'msg-1',
          threadId: 'thread-1',
          senderId: 'user-2',
          senderName: 'John Smith',
          content: 'Can you check Well 042 today? Pressure looks low.',
          timestamp: Date.now() - 1000 * 60 * 15,
          read: false,
        },
        unreadCount: 2,
        type: 'direct',
      },
      {
        id: 'thread-2',
        participants: [
          { id: 'user-3', name: 'Sarah Johnson', role: 'Field Manager' },
          { id: currentUserId, name: currentUserName, role: 'Pumper' },
          { id: 'user-4', name: 'Mike Davis', role: 'Pumper' },
        ],
        lastMessage: {
          id: 'msg-2',
          threadId: 'thread-2',
          senderId: 'user-3',
          senderName: 'Sarah Johnson',
          content: 'Great work on the route optimization today!',
          timestamp: Date.now() - 1000 * 60 * 60 * 2,
          read: true,
        },
        unreadCount: 0,
        type: 'group',
        title: 'North Field Team',
      },
      {
        id: 'thread-3',
        participants: [
          { id: currentUserId, name: currentUserName, role: 'Pumper' },
          { id: 'user-2', name: 'John Smith', role: 'Supervisor' },
        ],
        lastMessage: {
          id: 'msg-3',
          threadId: 'thread-3',
          senderId: currentUserId,
          senderName: currentUserName,
          content: 'Pump replaced, well is back online',
          timestamp: Date.now() - 1000 * 60 * 60 * 5,
          read: true,
        },
        unreadCount: 0,
        type: 'well',
        wellId: 'well-042',
        title: 'Well 042 Discussion',
      },
    ];

    setThreads(mockThreads);
  };

  const loadMessages = async (threadId: string) => {
    // Mock messages - in production, load from IndexedDB
    const mockMessages: Message[] = [
      {
        id: 'msg-1',
        threadId,
        senderId: 'user-2',
        senderName: 'John Smith',
        content: 'Hey, can you check Well 042 today?',
        timestamp: Date.now() - 1000 * 60 * 60,
        read: true,
      },
      {
        id: 'msg-2',
        threadId,
        senderId: currentUserId,
        senderName: currentUserName,
        content: 'Sure, I\'ll add it to my route',
        timestamp: Date.now() - 1000 * 60 * 55,
        read: true,
      },
      {
        id: 'msg-3',
        threadId,
        senderId: 'user-2',
        senderName: 'John Smith',
        content: 'Thanks! The pressure has been dropping over the last few days.',
        timestamp: Date.now() - 1000 * 60 * 50,
        read: true,
      },
      {
        id: 'msg-4',
        threadId,
        senderId: currentUserId,
        senderName: currentUserName,
        content: 'Just checked it. Pressure is at 450 PSI, looks normal to me.',
        timestamp: Date.now() - 1000 * 60 * 20,
        read: true,
      },
      {
        id: 'msg-5',
        threadId,
        senderId: 'user-2',
        senderName: 'John Smith',
        content: 'Great, thanks for the quick response! @' + currentUserName + ' can you send me a photo?',
        timestamp: Date.now() - 1000 * 60 * 15,
        read: false,
        mentions: [currentUserId],
      },
    ];

    setMessages(mockMessages);

    // Mark messages as read
    mockMessages.forEach(msg => {
      if (msg.senderId !== currentUserId && !msg.read) {
        indexedDBService.markMessageAsRead(msg.id);
      }
    });

    // Update thread unread count
    setThreads(prev =>
      prev.map(t =>
        t.id === threadId ? { ...t, unreadCount: 0 } : t
      )
    );
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedThread) return;

    const message: Message = {
      id: `msg-${Date.now()}`,
      threadId: selectedThread.id,
      senderId: currentUserId,
      senderName: currentUserName,
      content: newMessage,
      timestamp: Date.now(),
      read: false,
    };

    // Check for mentions
    const mentionRegex = /@(\w+)/g;
    const mentions = [...newMessage.matchAll(mentionRegex)].map(m => m[1]);
    if (mentions.length > 0) {
      message.mentions = mentions;
    }

    setMessages(prev => [...prev, message]);
    setNewMessage('');

    // Save to IndexedDB
    await indexedDBService.saveMessages([message]);

    // Update thread
    setThreads(prev =>
      prev.map(t =>
        t.id === selectedThread.id
          ? { ...t, lastMessage: message }
          : t
      )
    );

    toast.success('Message sent');
  };

  const handleStartVoiceRecording = () => {
    setIsRecording(true);
    toast.info('Recording voice message...');
    
    // Mock recording - in production, use MediaRecorder API
    setTimeout(() => {
      setIsRecording(false);
      toast.success('Voice message sent');
    }, 3000);
  };

  const handleStartVideoCall = () => {
    if (!selectedThread) return;
    
    const otherParticipant = selectedThread.participants.find(p => p.id !== currentUserId);
    toast.success(`Starting video call with ${otherParticipant?.name}...`);
    
    // In production, integrate with video call API (Twilio, Agora, etc.)
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = (timestamp: number): string => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const filteredThreads = threads.filter(thread =>
    thread.participants.some(p => p.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
    thread.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-4 shadow-lg">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold flex items-center space-x-2">
            <MessageSquare className="h-6 w-6" />
            <span>Messages</span>
          </h2>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/20 active:bg-white/30 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Thread List */}
        <div className={`${selectedThread ? 'hidden md:flex' : 'flex'} w-full md:w-80 flex-col border-r bg-gray-50`}>
          {/* Search */}
          <div className="p-3 border-b bg-white">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search conversations..."
                className="pl-10 h-10"
              />
            </div>
          </div>

          {/* Threads */}
          <div className="flex-1 overflow-y-auto">
            {filteredThreads.map(thread => {
              const otherParticipants = thread.participants.filter(p => p.id !== currentUserId);
              const displayName = thread.title || otherParticipants.map(p => p.name).join(', ');
              const isActive = selectedThread?.id === thread.id;

              return (
                <button
                  key={thread.id}
                  onClick={() => setSelectedThread(thread)}
                  className={`w-full text-left p-4 border-b transition-colors ${
                    isActive ? 'bg-purple-50 border-l-4 border-l-purple-600' : 'bg-white hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex items-center space-x-2 flex-1">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0">
                        {displayName.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium truncate ${isActive ? 'text-purple-900' : 'text-gray-900'}`}>
                          {displayName}
                        </p>
                        {thread.lastMessage && (
                          <p className="text-sm text-gray-600 truncate">
                            {thread.lastMessage.senderId === currentUserId ? 'You: ' : ''}
                            {thread.lastMessage.content}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end space-y-1 ml-2">
                      {thread.lastMessage && (
                        <span className="text-xs text-gray-500">
                          {formatTime(thread.lastMessage.timestamp)}
                        </span>
                      )}
                      {thread.unreadCount > 0 && (
                        <Badge className="bg-purple-600 text-white">
                          {thread.unreadCount}
                        </Badge>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Chat View */}
        {selectedThread ? (
          <div className="flex-1 flex flex-col bg-white">
            {/* Chat Header */}
            <div className="bg-white border-b p-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setSelectedThread(null)}
                  className="md:hidden p-2 -ml-2 rounded-full hover:bg-gray-100"
                >
                  ←
                </button>
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                  {(selectedThread.title || selectedThread.participants[0].name).charAt(0)}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">
                    {selectedThread.title || selectedThread.participants.filter(p => p.id !== currentUserId)[0].name}
                  </p>
                  <p className="text-xs text-gray-600">
                    {selectedThread.type === 'group' 
                      ? `${selectedThread.participants.length} members`
                      : 'Active now'}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-1">
                <button
                  onClick={() => toast.info('Voice call starting...')}
                  className="p-2 rounded-full hover:bg-gray-100 active:bg-gray-200"
                >
                  <Phone className="h-5 w-5 text-gray-600" />
                </button>
                <button
                  onClick={handleStartVideoCall}
                  className="p-2 rounded-full hover:bg-gray-100 active:bg-gray-200"
                >
                  <Video className="h-5 w-5 text-gray-600" />
                </button>
                <button className="p-2 rounded-full hover:bg-gray-100 active:bg-gray-200">
                  <MoreVertical className="h-5 w-5 text-gray-600" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {messages.map(message => {
                const isMine = message.senderId === currentUserId;
                const isMentioned = message.mentions?.includes(currentUserId);

                return (
                  <div
                    key={message.id}
                    className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[75%] ${isMine ? 'items-end' : 'items-start'} flex flex-col space-y-1`}>
                      {!isMine && (
                        <span className="text-xs font-medium text-gray-600 ml-2">
                          {message.senderName}
                        </span>
                      )}
                      <div
                        className={`rounded-2xl px-4 py-2 ${
                          isMine
                            ? 'bg-purple-600 text-white rounded-br-sm'
                            : isMentioned
                            ? 'bg-orange-100 text-orange-900 border-2 border-orange-300 rounded-bl-sm'
                            : 'bg-white text-gray-900 border border-gray-200 rounded-bl-sm'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                      </div>
                      <div className="flex items-center space-x-1 px-2">
                        <span className={`text-xs ${isMine ? 'text-gray-500' : 'text-gray-500'}`}>
                          {formatTime(message.timestamp)}
                        </span>
                        {isMine && (
                          message.read ? (
                            <CheckCheck className="h-3 w-3 text-blue-600" />
                          ) : (
                            <Check className="h-3 w-3 text-gray-400" />
                          )
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="bg-white border-t p-4">
              <div className="flex items-end space-x-2">
                <button className="p-2 rounded-full hover:bg-gray-100 active:bg-gray-200">
                  <Paperclip className="h-5 w-5 text-gray-600" />
                </button>
                
                <div className="flex-1 bg-gray-100 rounded-2xl flex items-end">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    placeholder="Type a message..."
                    className="border-0 bg-transparent focus:ring-0 resize-none"
                    rows={1}
                  />
                  <button
                    onClick={() => setShowMentions(!showMentions)}
                    className="p-2 rounded-full hover:bg-gray-200 active:bg-gray-300 mr-1"
                  >
                    <AtSign className="h-5 w-5 text-gray-600" />
                  </button>
                </div>

                {isRecording ? (
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center animate-pulse">
                    <Mic className="h-5 w-5 text-white" />
                  </div>
                ) : newMessage.trim() ? (
                  <button
                    onClick={handleSendMessage}
                    className="w-12 h-12 bg-purple-600 hover:bg-purple-700 rounded-full flex items-center justify-center transition-colors"
                  >
                    <Send className="h-5 w-5 text-white" />
                  </button>
                ) : (
                  <button
                    onClick={handleStartVoiceRecording}
                    className="w-12 h-12 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center transition-colors"
                  >
                    <Mic className="h-5 w-5 text-gray-600" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="hidden md:flex flex-1 items-center justify-center bg-gray-50">
            <div className="text-center">
              <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Select a conversation to start messaging</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
